import { staircaseConfig, timing, variantForLevel } from './params';
const ESTIMATE_TOLERANCE = 0.05;
function clampDeadline(deadlineMs) {
    return Math.max(staircaseConfig.minValue, Math.min(staircaseConfig.maxValue, deadlineMs));
}
function pickOpForLevel(level, trialIdx) {
    const variant = variantForLevel(level);
    const ops = variant.ops;
    // Distribution uniforme. trialIdx + Math.random combine pour replay manuel
    // (tests utilisent makeStimulus directement, donc Math.random ici est OK).
    const idx = Math.floor(Math.random() * ops.length);
    return ops[idx % ops.length];
}
function randInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
function computeAnswer(a, b, op) {
    if (op === 'add')
        return a + b;
    if (op === 'sub')
        return a - b;
    if (op === 'mul')
        return a * b;
    if (op === 'div')
        return Math.round(a / b);
    // estimate : a + b par convention (calcule cote makeStimulus avant mutation).
    return a + b;
}
/**
 * Genere un stimulus arithmetique adapte au level, avec deadline clampe.
 *
 * Garde-fous :
 *   - sub : a >= b (no negatives).
 *   - div : a = b * c (no remainders).
 *   - estimate : equation reelle, puis valeur affichee mutee +/- offset 1-15%.
 */
export function makeStimulus(trialIdx, deadlineMs, level) {
    const variant = variantForLevel(level);
    const op = pickOpForLevel(level, trialIdx);
    const clampedDeadline = clampDeadline(deadlineMs);
    if (op === 'add') {
        const a = randInt(1, variant.maxOperand);
        const b = randInt(1, variant.maxOperand);
        return {
            trialIdx,
            deadlineMs: clampedDeadline,
            a,
            b,
            op,
            answer: a + b,
        };
    }
    if (op === 'sub') {
        const aRaw = randInt(1, variant.maxOperand);
        const bRaw = randInt(1, variant.maxOperand);
        const a = Math.max(aRaw, bRaw);
        const b = Math.min(aRaw, bRaw);
        return {
            trialIdx,
            deadlineMs: clampedDeadline,
            a,
            b,
            op,
            answer: a - b,
        };
    }
    if (op === 'mul') {
        const cap = Math.min(12, variant.maxOperand);
        const a = randInt(2, cap);
        const b = randInt(2, cap);
        return {
            trialIdx,
            deadlineMs: clampedDeadline,
            a,
            b,
            op,
            answer: a * b,
        };
    }
    if (op === 'div') {
        const b = randInt(2, 12);
        const c = randInt(2, 12);
        const a = b * c;
        return {
            trialIdx,
            deadlineMs: clampedDeadline,
            a,
            b,
            op,
            answer: c,
        };
    }
    // estimate : on genere une vraie equation (add/sub/mul) puis on mute la
    // valeur affichee de +/- 1..15%. La reponse exacte reste l'answer reel
    // (utilise par l'algo de comparison) ; estimateTrue indique si la valeur
    // affichee est "assez proche" (tolerance 5%).
    const baseOp = Math.random() < 0.5 ? 'add' : Math.random() < 0.5 ? 'sub' : 'mul';
    let a;
    let b;
    if (baseOp === 'mul') {
        a = randInt(2, 12);
        b = randInt(2, 12);
    }
    else if (baseOp === 'sub') {
        const aRaw = randInt(1, variant.maxOperand);
        const bRaw = randInt(1, variant.maxOperand);
        a = Math.max(aRaw, bRaw);
        b = Math.min(aRaw, bRaw);
    }
    else {
        a = randInt(1, variant.maxOperand);
        b = randInt(1, variant.maxOperand);
    }
    const answer = computeAnswer(a, b, baseOp);
    const offsetRatio = (randInt(1, 15) / 100) * (Math.random() < 0.5 ? -1 : 1);
    const estimatedValue = Math.max(0, Math.round(answer * (1 + offsetRatio)));
    const ratioFromTruth = answer === 0 ? 0 : Math.abs(estimatedValue - answer) / Math.abs(answer);
    const estimateTrue = ratioFromTruth <= ESTIMATE_TOLERANCE;
    return {
        trialIdx,
        deadlineMs: clampedDeadline,
        a,
        b,
        op: 'estimate',
        answer,
        estimatedValue,
        estimateTrue,
    };
}
export function createNumberCrunchAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        inputBuffer: '',
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settleNumeric(picked, correct) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            picked,
            correct,
            reactionTimeMs: rt,
        });
    }
    function settleEstimate(pick, correct) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            picked: null,
            estimatePick: pick,
            correct,
            reactionTimeMs: rt,
        });
    }
    function settleTimeout() {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            picked: null,
            correct: false,
            reactionTimeMs: rt,
        });
    }
    return {
        mount() {
            // Idempotent, rendu declaratif cote React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    inputBuffer: '',
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'equation' });
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        trialStart = Date.now();
                        // Timeout deadline : si pas de reponse, on resout incorrect.
                        timers.push(setTimeout(() => {
                            if (resolver) {
                                settleTimeout();
                            }
                        }, stim.deadlineMs));
                    }, timing.equationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleDigit(digit) {
            if (state.subPhase !== 'awaiting_input' || !state.stim)
                return;
            if (state.stim.op === 'estimate')
                return;
            const newBuffer = `${state.inputBuffer}${digit}`;
            // Cap buffer length for safety (answer rarely > 6 digits).
            if (newBuffer.length > 6)
                return;
            update({ inputBuffer: newBuffer });
            const parsed = Number.parseInt(newBuffer, 10);
            if (Number.isFinite(parsed) && parsed === state.stim.answer) {
                settleNumeric(parsed, true);
            }
        },
        handleClear() {
            if (state.subPhase !== 'awaiting_input')
                return;
            update({ inputBuffer: '' });
        },
        handleEstimate(pick) {
            if (state.subPhase !== 'awaiting_input' || !state.stim)
                return;
            if (state.stim.op !== 'estimate')
                return;
            const truth = state.stim.estimateTrue ?? false;
            const correct = (pick === 'true') === truth;
            settleEstimate(pick, correct);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map